import { skipToken } from '@tanstack/react-query';
import BacktestQuarantineNotice from '@/components/Trade/BacktestQuarantineNotice';
import React, { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/Layout/AdminLayout';
import Alert from '@/components/UI/Alert';
import { Button, Card, DataTable } from '@components/UI';
import { sanitizeNumericDraft } from '@components/UI/numericDraft';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import EntityLink from '@/components/Entity/EntityLink.tsx';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog.tsx';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { formatTradeLogicV2ExitCandidateLabel } from '@/components/TradeLogic/formatTradeLogicV2ExitCandidateLabel.ts';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { TradingStrategyBacktestDetailed as BacktestDetailed, TradingStrategyBacktestTrade as BacktestTrade } from '@/types/appRouter';
import PriceEntrySetupSummary from '@/components/TradeLogic/PriceEntrySetupSummary';

const BacktestSummary: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const backtestId = Number(searchParams.get('backtestId') || '0');

    const [inputBacktestId, setInputBacktestId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (inputBacktestId.trim()) {
            setSearchParams({ backtestId: inputBacktestId.trim() });
        }
    };

    const backtestQuery = useAppQuery(
        {
            ...trpc.backtest.fetchBacktest.queryOptions({
                backtestId,
                shouldFetchTrades: true
            }),
            enabled: backtestId > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch backtest',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    const backtest: BacktestDetailed | null = useMemo(() => backtestQuery.data?.backtest || null, [backtestQuery.data?.backtest]);

    const entityQuery = useAppQuery(
        {
            ...trpc.entity.details.queryOptions(
                backtest && backtest.entityType !== 'FuturesProduct'
                    ? { entityId: backtest.entityId, entityType: backtest.entityType }
                    : skipToken
            )
        },
        {
            fallbackErrorMessage: 'Failed to fetch entity details',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    const entity = useMemo(() => entityQuery.data?.entity || null, [entityQuery.data?.entity]);

    const deleteBacktestMutation = useTrpcMutation(
        trpc.backtest.deleteBacktestsByIds.mutationOptions({
            onSuccess: () => {
                void navigate('/tools/filterBacktests');
            }
        }),
        {
            successMessage: 'Backtest deleted successfully',
            fallbackErrorMessage: 'Failed to delete backtest'
        }
    );

    const trades: BacktestTrade[] = useMemo(() => backtest?.trades || [], [backtest?.trades]);

    const tradesColumns: ColumnDef<BacktestTrade>[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'Trade ID',
                size: 90
            },
            {
                accessorKey: 'dateTime',
                header: 'Date / time',
                size: 180,
                cell: (info) => {
                    const raw = info.getValue() as string;
                    return new Date(raw).toLocaleString();
                }
            },
            {
                accessorKey: 'direction',
                header: 'Position side',
                size: 90
            },
            {
                accessorKey: 'didEnterTrade',
                header: 'Entered?',
                size: 80,
                cell: (info) => ((info.getValue() as number) === 1 ? 'Yes' : 'No')
            },
            {
                accessorKey: 'profit',
                header: 'Profit',
                size: 100,
                cell: (info) => {
                    const profit = info.getValue() as number | null;

                    if (profit === null || Number.isNaN(profit)) {
                        return <span className="text-xs text-zinc-400">—</span>;
                    }

                    const colorClass = profit > 0 ? 'text-green-400' : profit < 0 ? 'text-red-400' : '';
                    return <span className={colorClass}>${profit.toFixed(2)}</span>;
                }
            },
            {
                accessorKey: 'profitPercentage',
                header:
                    backtest?.entityType === 'FuturesProduct'
                        ? backtest.quarantine
                            ? 'Stored return %'
                            : 'Net price return %'
                        : 'Profit %',
                size: 100,
                cell: (info) => {
                    const profitPct = info.getValue() as number | null;

                    if (profitPct === null || Number.isNaN(profitPct)) {
                        return <span className="text-xs text-zinc-400">—</span>;
                    }

                    const colorClass = profitPct > 0 ? 'text-green-400' : profitPct < 0 ? 'text-red-400' : '';
                    return <span className={colorClass}>{profitPct.toFixed(2)}%</span>;
                }
            },
            ...(backtest?.entityType === 'FuturesProduct'
                ? [
                      {
                          accessorKey: 'returnOnResearchReservePercentage',
                          header: 'Return on research reserve %',
                          size: 190,
                          cell: (info) => {
                              const value = info.getValue();
                              return typeof value === 'number' ? value.toFixed(2) + '%' : '—';
                          }
                      } satisfies ColumnDef<BacktestTrade>
                  ]
                : []),
            {
                accessorKey: 'predictedProfit',
                header: 'Predicted %',
                size: 110,
                cell: (info) => {
                    const raw = info.getValue() as string | null;

                    if (!raw) {
                        return <span className="text-xs text-zinc-400">—</span>;
                    }

                    const parsed = Number.parseFloat(raw);
                    const value = Number.isNaN(parsed) ? raw : `${parsed.toFixed(2)}%`;

                    return <span>{value}</span>;
                }
            },
            {
                id: 'tradeLogicV2ExitCandidate',
                header: 'Exit candidate',
                size: 110,
                cell: () => {
                    return formatTradeLogicV2ExitCandidateLabel({
                        exitCandidateHash: backtest?.exitCandidateHash ?? null,
                        exitCandidateId: backtest?.exitCandidateId ?? null
                    });
                }
            },
            {
                accessorKey: 'skipReason',
                header: 'Skip reason',
                size: 260,
                cell: (info) => {
                    const reason = info.getValue() as string | null;
                    if (!reason) return <span className="text-xs text-zinc-500">—</span>;

                    const truncated = reason.length > 120 ? `${reason.slice(0, 117)}…` : reason;
                    return (
                        <span className="text-xs text-zinc-300" title={reason}>
                            {truncated}
                        </span>
                    );
                }
            }
        ],
        [backtest?.entityType, backtest?.exitCandidateHash, backtest?.exitCandidateId, backtest?.quarantine]
    );

    const isLoading = backtestQuery.isLoading || entityQuery.isLoading;

    if (isLoading) {
        return (
            <AdminLayout containerSize="1600px" title="Backtest Summary">
                <div className="flex justify-center my-5">
                    <DataLoadingSpinner text="Loading backtest summary..." />
                </div>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout containerSize="1600px" title="Backtest Summary">
                <Alert variant="danger">{error}</Alert>
            </AdminLayout>
        );
    }

    if (backtestId === 0) {
        return (
            <AdminLayout containerSize="1600px" title="Backtest Summary">
                <Card className="mb-4">
                    <div className="p-4">
                        <h4 className="mb-3">Enter Backtest ID</h4>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium mb-1" htmlFor="backtestId">
                                    Backtest ID
                                </label>
                                <input
                                    required
                                    className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-blue-500 focus:outline-none"
                                    id="backtestId"
                                    inputMode="numeric"
                                    placeholder="Enter backtest ID"
                                    type="text"
                                    value={inputBacktestId}
                                    onChange={(event) =>
                                        setInputBacktestId(
                                            sanitizeNumericDraft(event.target.value, { integer: true, allowNegative: false })
                                        )
                                    }
                                />
                                <div className="mt-1 text-sm text-zinc-400">Enter a backtest ID to view its summary and trade list.</div>
                            </div>
                            <Button type="submit" variant="primary">
                                View Backtest Summary
                            </Button>
                        </form>
                    </div>
                </Card>
            </AdminLayout>
        );
    }

    if (!backtest) {
        return (
            <AdminLayout containerSize="1600px" title="Backtest Summary">
                <Alert variant="warning">No backtest data found</Alert>
            </AdminLayout>
        );
    }

    const {
        id,
        profit,
        percentageGain,
        avgTradeProfit,
        winRate,
        numTrades,
        maxDrawdown,
        returnOnRiskCapital,
        avgProfitPerDayPercent,
        medianProfitPercent,
        startDateTime,
        endDateTime,
        lastFullRefreshDateTime,
        lastUpdateDateTime,
        exitCandidateId,
        exitCandidateHash,
        optionBuyingStrategyIdLeg1,
        optionBuyingStrategyIdLeg2,
        optionBuyingStrategyIdLeg3,
        optionBuyingStrategyIdLeg4,
        optionBuyingStrategyIdLeg5,
        optionBuyingStrategyIdLeg6
    } = backtest;

    const optionBuyingStrategyIds = [
        optionBuyingStrategyIdLeg1,
        optionBuyingStrategyIdLeg2,
        optionBuyingStrategyIdLeg3,
        optionBuyingStrategyIdLeg4,
        optionBuyingStrategyIdLeg5,
        optionBuyingStrategyIdLeg6
    ].filter((strategyIdValue): strategyIdValue is number => strategyIdValue !== null && strategyIdValue !== undefined);

    const handleDeleteConfirm = async () => {
        if (!id) return;
        await deleteBacktestMutation.mutateAsync({ ids: [id] });
        setIsDeleteConfirmOpen(false);
    };

    const title = `Backtest Summary - #${id}`;

    return (
        <AdminLayout containerSize="1600px" title={title}>
            <BacktestQuarantineNotice quarantine={backtest.quarantine} />
            {backtest.entityType === 'FuturesProduct' && !backtest.quarantine && (
                <p className="mb-4 text-sm text-zinc-400">
                    Futures percentage exits use the signed price move from the dated-contract entry. Net price returns include fees.
                    Research-reserve returns reflect the configured allocation, not broker margin.
                </p>
            )}
            <Card className="mb-4">
                <div className="p-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-2 text-sm text-zinc-200">
                        <div className="text-lg font-semibold text-zinc-100">
                            Backtest #{id}{' '}
                            {backtest.strategy?.name && <span className="text-zinc-400 text-sm">– {backtest.strategy.name}</span>}
                        </div>
                        <div className="flex flex-wrap gap-4">
                            <div>
                                <div className="text-xs text-zinc-400">Entity</div>
                                <div className="font-mono text-sm">
                                    {entity ? (
                                        <EntityLink showName showSymbol entity={entity} />
                                    ) : (
                                        `${backtest.entityId}-${backtest.entityType}`
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Strategy ID</div>
                                <div className="font-mono text-sm">{backtest.strategyId}</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Exit candidate</div>
                                <div className="font-mono text-sm">
                                    {formatTradeLogicV2ExitCandidateLabel({
                                        exitCandidateHash,
                                        exitCandidateId
                                    })}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Option strategies</div>
                                <div className="font-mono text-xs">
                                    {optionBuyingStrategyIds.length ? optionBuyingStrategyIds.join(', ') : 'None'}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                            <div>
                                <div className="text-xs text-zinc-400">Profit</div>
                                <div className={profit > 0 ? 'text-green-400' : profit < 0 ? 'text-red-400' : 'text-zinc-200'}>
                                    ${profit.toFixed(2)}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Return %</div>
                                <div
                                    className={
                                        percentageGain > 0 ? 'text-green-400' : percentageGain < 0 ? 'text-red-400' : 'text-zinc-200'
                                    }
                                >
                                    {percentageGain.toFixed(2)}%
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Avg trade return</div>
                                <div className="text-zinc-200">{avgTradeProfit.toFixed(2)}%</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Win rate</div>
                                <div className="text-zinc-200">{winRate.toFixed(2)}%</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Trades</div>
                                <div className="text-zinc-200">{numTrades}</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Max drawdown</div>
                                <div className="text-zinc-200">{maxDrawdown.toFixed(2)}%</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">
                                    {backtest.entityType === 'FuturesProduct' ? 'Return on research reserve' : 'Return on risk capital'}
                                </div>
                                <div className="text-zinc-200">{returnOnRiskCapital.toFixed(2)}%</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Median trade %</div>
                                <div className="text-zinc-200">{medianProfitPercent.toFixed(2)}%</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Avg profit/day %</div>
                                <div className="text-zinc-200">{avgProfitPerDayPercent.toFixed(2)}%</div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Start / End</div>
                                <div className="text-xs text-zinc-300">
                                    {new Date(startDateTime).toLocaleDateString()} – {new Date(endDateTime).toLocaleDateString()}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-zinc-400">Updated</div>
                                <div className="text-xs text-zinc-300">
                                    Last full: {lastFullRefreshDateTime || 'N/A'}
                                    <br />
                                    Last update: {lastUpdateDateTime}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                                navigate('/tools/filterBacktests', {
                                    state: {
                                        strategyId: backtest.strategyId,
                                        entityType: backtest.entityType,
                                        autoSearch: true
                                    }
                                })
                            }
                        >
                            Open in FilterBacktests
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => navigate(`/tools/backtestTrades?backtestId=${id.toString()}`)}>
                            Open full Trades view
                        </Button>
                        <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                                setIsDeleteConfirmOpen(true);
                            }}
                        >
                            Delete backtest
                        </Button>
                    </div>
                </div>
            </Card>

            <PriceEntrySetupSummary summary={backtestQuery.data?.priceEntrySetups} />

            <Card>
                <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-lg font-semibold text-zinc-100">Trades and skipped signals</h4>
                        <div className="text-xs text-zinc-400">
                            {numTrades} entered, {trades.filter((trade) => !trade.didEnterTrade).length} skipped ·
                            <span className="ml-1">
                                <Link className="text-blue-400 hover:underline" to={`/tools/backtestTrades?backtestId=${id.toString()}`}>
                                    open full Trades view
                                </Link>
                            </span>
                        </div>
                    </div>
                    {trades.length === 0 ? (
                        <div className="text-sm text-zinc-400">No signals found for this backtest.</div>
                    ) : (
                        <DataTable
                            striped
                            columns={tradesColumns}
                            data={trades}
                            pagination={{ enabled: true, pageSize: 25 }}
                            search={false}
                        />
                    )}
                </div>
            </Card>

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Delete backtest"
                confirmVariant="danger"
                isOpen={isDeleteConfirmOpen}
                message="Are you sure you want to delete this backtest? This action cannot be undone."
                title="Delete Backtest"
                onCancel={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleDeleteConfirm}
            />
        </AdminLayout>
    );
};

export default BacktestSummary;
